import { ArrayUtils, StringUtils, Logger, engineComponent, Utils } from "neo-common";
import { ParticleStyle } from "../Styles/ParticleStyle";
import { StyleManager } from "../../Managers/StyleManager";
import { ComponentTypes } from "../../ComponentTypes";
import { AnimationFrameBaseElement } from "../../../AnimationFrame/AnimationFrame";
import { StageController } from "../../StageController";
import { CustomUtils } from "../../../Utils/CustomUtils";

var protonProto: any = window["Proton"];

@engineComponent(ComponentTypes.DisplayObject)
export class ProtonElement extends AnimationFrameBaseElement {
    static Pool: Object = {};

    private _animationName: string = "";

    // style is mandatory
    private _style: ParticleStyle;
    private _styleName: string;
    private _proton: Proton.Proton;
    private _renderer: Proton.Renderer;
    private _emitters: Array<Proton.Emitter>;

    private _tint: number;
    private _blendMode: number;
    private _emitting: boolean;
    private _stopping: boolean;
    private _textures: Array<PIXI.Texture>;
    private _animationSpeed: number;
    private _animationLoop: boolean;

    constructor() {
        super();

        this._emitting = false;
        this._stopping = true;
        //this._stopped = true;
        this._animationSpeed = 1;
        this._animationLoop = true;
        this._emitters = [];
        this._proton = new protonProto() as Proton.Proton;

        this.onProtonUpdate = this.onProtonUpdate.bind(this);
        this.onParticleCreated = this.onParticleCreated.bind(this);
        this.onParticleUpdate = this.onParticleUpdate.bind(this);
        this.onParticleDead = this.onParticleDead.bind(this);
        this.transformSprite = this.transformSprite.bind(this);
        this.alphaDead = this.alphaDead.bind(this);
        this.scaleDead = this.scaleDead.bind(this);
    }

    protected frame_onFrame(): void {
        this._proton.update();
    }

    protected get frame_name(): string {
        return this._styleName;
    }

    public updateByNode(node: Element): void {
        this.create(node);

        this.super2.runTween(node);
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    create(node: Element): void {
        this.super2.createByNode(node);

        if (node.hasAttribute(ProtonElementAttributes.TINT)) {
            this._tint = parseInt(node.getAttribute(ProtonElementAttributes.TINT), 16);
        }

        if (node.hasAttribute(ProtonElementAttributes.BLEND_MODE)) {
            this._blendMode = parseInt(node.getAttribute(ProtonElementAttributes.BLEND_MODE));
        }

        if (node.hasAttribute(ProtonElementAttributes.STYLE) || node.hasAttribute(ProtonElementAttributes.STYLE_2)) {
            if (node.hasAttribute(ProtonElementAttributes.STYLE)) {
                this._style = <ParticleStyle>StyleManager.Styles[node.getAttribute(ProtonElementAttributes.STYLE)];
                this._styleName = this._style.node.nodeName;
            } else {
                this._style = <ParticleStyle>StyleManager.Styles[node.getAttribute(ProtonElementAttributes.STYLE_2)];
                this._styleName = this._style.node.nodeName;
            }

            if (this._style != null) {
                var next: Element = this._style.node.firstElementChild;
                while (next != null) {
                    if (next.nodeName === "Emitter") {
                        var emitter: Proton.Emitter = new Proton.Emitter();
                        this._emitters.push(emitter);

                        var emitterNext: Element = next.firstElementChild;
                        while (emitterNext != null) {
                            switch (emitterNext.nodeName) {
                                case "Rate":
                                    if (emitterNext.hasAttribute("Rate")) {
                                        let rate: Array<number> = ArrayUtils.numberArray(
                                            emitterNext.getAttribute("Rate"),
                                        );
                                        if (rate.length === 2) {
                                            emitter.rate = new Proton.Rate(rate[0], rate[1]);
                                        } else {
                                            if (DEBUG) {
                                                this.log(
                                                    "WARNING Rate has different number of parameters. It has to have two parameters.",
                                                );
                                            }
                                        }
                                    } else if (
                                        emitterNext.hasAttribute("RateStart") &&
                                        emitterNext.hasAttribute("RateEnd")
                                    ) {
                                        /**
                                         * @deprecated Misleading naming of RateStart. Instead this use NumPan.
                                         * @type {Array<number>}
                                         */
                                        let rateStart: Array<number> = ArrayUtils.numberArray(
                                            emitterNext.getAttribute("RateStart"),
                                        );
                                        /**
                                         * @deprecated Misleading naming of RateEnd. Instead this use TimePan.
                                         * @type {Array<number>}
                                         */
                                        let rateEnd: Array<number> = ArrayUtils.numberArray(
                                            emitterNext.getAttribute("RateEnd"),
                                        );
                                        emitter.rate = new Proton.Rate(
                                            new Proton.Span(rateStart[0], rateStart[1]),
                                            new Proton.Span(rateEnd[0], rateEnd[1]),
                                        );
                                    } else if (
                                        emitterNext.hasAttribute("NumPan") &&
                                        emitterNext.hasAttribute("TimePan")
                                    ) {
                                        let numPan: Array<number> = ArrayUtils.numberArray(
                                            emitterNext.getAttribute("NumPan"),
                                        );
                                        let timePan: Array<number> = ArrayUtils.numberArray(
                                            emitterNext.getAttribute("TimePan"),
                                        );
                                        emitter.rate = new Proton.Rate(
                                            new Proton.Span(numPan[0], numPan[1]),
                                            new Proton.Span(timePan[0], timePan[1]),
                                        );
                                    } else {
                                        if (DEBUG) {
                                            this.log("WARNING Rate has wrong attributes");
                                        }
                                    }
                                    break;
                                case "Initializers":
                                    this.createInitializers(emitterNext, this._emitters[this._emitters.length - 1]);
                                    break;
                                case "Behaviours":
                                    this.createBehaviours(emitterNext, this._emitters[this._emitters.length - 1]);
                                    break;
                                default:
                                    if (DEBUG) {
                                        this.log("WARNING unknown type for emitter");
                                    }
                            }
                            emitterNext = emitterNext.nextElementSibling;
                        }

                        if (next.hasAttribute("Emit")) {
                            emitter.emit(next.getAttribute("Emit"));
                        } else {
                            emitter.emit();
                        }

                        if (next.hasAttribute("X")) {
                            emitter["p"].x = parseInt(next.getAttribute("X"));
                        }

                        if (next.hasAttribute("Y")) {
                            emitter["p"].y = parseInt(next.getAttribute("Y"));
                        }
                    }
                    next = next.nextElementSibling;
                }

                if (this._emitters != null) {
                    for (var i = 0; i < this._emitters.length; i++) {
                        this._proton.addEmitter(this._emitters[i]);
                    }

                    this._renderer = new Proton.Renderer("other", this._proton);
                    this._renderer.onProtonUpdate = this.onProtonUpdate;
                    this._renderer.onParticleCreated = this.onParticleCreated;
                    this._renderer.onParticleUpdate = this.onParticleUpdate;
                    this._renderer.onParticleDead = this.onParticleDead;
                    this._emitting = false;
                    this.frame_stop();
                } else {
                    if (DEBUG) {
                        this.log("ERROR no emitters");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no style with name: " + node.getAttribute(ProtonElementAttributes.STYLE));
                }
            }
        }

        if (node.hasAttribute(ProtonElementAttributes.START)) {
            if (this.isFrameStopped) {
                if (this._renderer != null) {
                    this._renderer.stop();
                }

                this.removeChilds();

                if (this._emitters != null && this._emitters.length > 0) {
                    if (node.hasAttribute(ProtonElementAttributes.EMIT)) {
                        for (var i: number = 0; i < this._emitters.length; i++) {
                            this._emitters[i].emit(node.getAttribute(ProtonElementAttributes.EMIT));
                        }
                    }
                }
                if (this._renderer != null) {
                    this._renderer.start();
                    this._stopping = false;
                    this._emitting = true;
                    this.frame_start();
                }
            } else if (this._stopping) {
                this._emitting = true;
                this._stopping = false;
            }
        }

        if (node.hasAttribute(ProtonElementAttributes.STOPPING)) {
            this._emitting = false;
            this._stopping = true;
        }

        if (node.hasAttribute(ProtonElementAttributes.STOP)) {
            this.stop();
        }
    }

    start(emit: string = "") {
        if (this.isFrameStopped) {
            /*
            if (this._renderer != null) {
                this._renderer.stop();
            }
            */

            this.removeChilds();

            if (this._emitters != null && this._emitters.length > 0) {
                if (emit != "") {
                    for (var i: number = 0; i < this._emitters.length; i++) {
                        //this._emitters[i]['age'] = 1;
                        //this._emitters[i].stopEmit();
                        //this._emitters[i].removeAllParticles();
                        this._emitters[i].emit(emit);
                    }
                }
            }
            if (this._renderer != null) {
                this._renderer.start();
                this._stopping = false;
                this._emitting = true;
                this.frame_start();
            }
        }
    }

    private onProtonUpdate(): void {
        var emmiter0 = this._emitters[0];

        if (emmiter0["emitTotalTimes"] === "none" && emmiter0["particles"].length === 0) {
            this._emitting = false;
            this.frame_stop();
            this._renderer.stop();

            this.removeChilds();
        }
    }

    private onParticleCreated(particle: any): void {
        if (this._stopping === false) {
            particle.sprite = null;
            if (particle.sprite != null) {
                if (this._textures != null && this._textures.length > 0) {
                    ProtonElement.Pool[this._animationName].push(particle.sprite);
                    this.super2.removeChild(particle.sprite);
                } else {
                    ProtonElement.Pool[particle.target.textureCacheIds[0]].push(particle.sprite);
                    this.super2.removeChild(particle.sprite);
                }
            }
            let particleSprite: any;
            if (this._textures != null && this._textures.length > 0) {
                if (ProtonElement.Pool[this._animationName] == null) {
                    ProtonElement.Pool[this._animationName] = [];
                }

                /*
                particleSprite = new PIXI.extras.AnimatedSprite(this._textures);
                particleSprite.name = this._animationName;
                */

                if (ProtonElement.Pool[this._animationName].length === 0) {
                    particleSprite = new PIXI.extras.AnimatedSprite(this._textures);
                    particleSprite.name = this._animationName;
                } else {
                    particleSprite = ProtonElement.Pool[this._animationName].shift();
                }

                particleSprite.loop = this._animationLoop;
                particleSprite.animationSpeed = this._animationSpeed;
                if (!this._animationLoop) {
                    particleSprite.gotoAndStop(0);
                }
                particleSprite.play();
            } else {
                if (ProtonElement.Pool[particle.target.textureCacheIds[0]] == null) {
                    ProtonElement.Pool[particle.target.textureCacheIds[0]] = [];
                }

                if (ProtonElement.Pool[particle.target.textureCacheIds[0]].length === 0) {
                    particleSprite = new PIXI.Sprite(particle.target);
                    particleSprite.name = particle.target.textureCacheIds[0];
                } else {
                    particleSprite = ProtonElement.Pool[particle.target.textureCacheIds[0]].shift();
                }
            }
            if (this._tint != null) {
                particleSprite.tint = this._tint;
            }

            if (this._blendMode != null) {
                particleSprite.blendMode = CustomUtils.getBlendMode(this._blendMode);
            }

            particleSprite.visible = true;
            particle.sprite = particleSprite;
            this.super2.addChild(particleSprite);
        }
    }

    private onParticleUpdate(particle: any): void {
        if (particle != null && particle.sprite != null) {
            this.transformSprite(particle.sprite, particle);
        }
    }

    private onParticleDead(particle: any) {
        if (particle.sprite != null) {
            particle.sprite.visible = false;
            if (particle.sprite instanceof PIXI.extras.AnimatedSprite) {
                particle.sprite.stop();
            }
            this.super2.removeChild(particle.sprite);
        }

        particle.sprite = null;
        particle.dead = true;
        particle = null;
    }

    /**
     * Transform sprite in particle with proton.
     * @method transformSprite
     * @param particleSprite any
     * @param particle any
     * @return void
     */
    private transformSprite(particleSprite: any, particle: any) {
        if (
            particleSprite.transform != null &&
            !Utils.isNullOrUndefined(particleSprite) &&
            !Utils.isNullOrUndefined(particle)
        ) {
            particleSprite.position.x = particle.p.x;
            particleSprite.position.y = particle.p.y;
            particleSprite.scale.x = particle.scale;
            particleSprite.scale.y = particle.scale;
            particleSprite.anchor.x = 0.5;
            particleSprite.anchor.y = 0.5;
            particleSprite.alpha = particle.alpha;
            particleSprite.rotation = (particle.rotation * Math.PI) / 180;
        } else {
            particle.dead = true;
        }
    }

    /**
     * Stop emitting particles.
     * @method stop
     * @return void
     */
    stop(): void {
        this._emitting = false;
        this._stopping = true;
        this.frame_stop();

        for (var i: number = 0; i < this._emitters.length; i++) {
            this._emitters[i].resetParticles();
        }

        if (this._renderer != null) {
            this._renderer.stop();
        }

        this.removeChilds();
    }

    removeChilds(): void {
        if (this.super2.children != null) {
            let i: number = 0;
            while (this.super2.children.length !== 0) {
                if (this.super2.children[0] != null) {
                    if (this._textures != null && this._textures.length > 0) {
                        (this.super2.children[0] as PIXI.extras.AnimatedSprite).stop();
                        ProtonElement.Pool[this._animationName].push(this.super2.children[0]);
                    } else {
                        ProtonElement.Pool[this.super2.children[0].name].push(this.super2.children[0]);
                    }
                    this.super2.removeChild(this.super2.children[0]);
                }
            }
        }
    }

    internalDestroy(options?: PIXI.DestroyOptions | boolean): void {
        this._emitting = false;
        this._stopping = true;
        this.frame_stop();
        this._textures = null;

        if (this._emitters != null && this._emitters.length > 0) {
            for (var i = 0; i < this._emitters.length; i++) {
                if (this._emitters[i] != null) {
                    this._proton.removeEmitter(this._emitters[i]);
                    this._emitters[i].destroy();
                    this._emitters[i] = null;
                }
            }
        }

        // removeEmitter
        // particleSleep
        // destroy

        if (this._renderer != null) {
            this._renderer.stop();
            this._renderer = null;
        }

        this._proton.destroy();
        this._proton = null;

        this.removeChilds();
    }

    /**
     * Create initializers from xml for emitter instance.
     * Initializers create start values and behaviour for particle instances.
     * @method createInitializers
     * @param node is Element from xml
     * @param emitter Proton.Emitter
     * @return void
     */
    private createInitializers(node: Element, emitter: Proton.Emitter): void {
        var next: Element = node.firstElementChild;
        while (next != null) {
            switch (next.nodeName) {
                case "ImageTarget":
                    var imageTarget: Proton.ImageTarget = this.addImageTarget(next);
                    if (imageTarget != null) {
                        emitter.addInitialize(imageTarget);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING ImageTarget is null");
                        }
                    }
                    break;
                case "AnimationTarget":
                    this.addAnimationTarget(next);
                    break;
                case "Radius":
                    var radius: Proton.Radius = this.addRadius(next);
                    if (radius != null) {
                        emitter.addInitialize(radius);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Radius is null");
                        }
                    }
                    break;
                case "Mass":
                    var mass: Proton.Mass = this.addMass(next);
                    if (mass != null) {
                        emitter.addInitialize(mass);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Mass is null");
                        }
                    }
                    break;
                case "Life":
                    var life: Proton.Life = this.addLife(next);
                    if (life != null) {
                        emitter.addInitialize(life);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Life is null");
                        }
                    }
                    break;
                case "Position":
                    var position: Proton.Position = this.addPosition(next);
                    if (position != null) {
                        emitter.addInitialize(position);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Position is null");
                        }
                    }
                    break;
                case "Velocity":
                    var velocity: Proton.Velocity = this.addVelocity(next);
                    if (velocity != null) {
                        emitter.addInitialize(velocity);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Velocity is null");
                        }
                    }
                    break;
                default:
                    if (DEBUG) {
                        this.log("WARNING unknown initializer");
                    }
            }
            next = next.nextElementSibling;
        }
    }

    /**
     * Add initializer ImageTarget.
     * This initializer add images instead of shapes as particles.
     * Example:
     *   <ImageTarget Image="IMAGE_1;IMAGE_2;IMAGE_3;IMAGE_4;IMAGE_5;IMAGE_6" />
     * @method addImageTarget
     * @param node is Element from xml
     * @return void
     */
    private addImageTarget(node: Element): Proton.Radius {
        if (node.hasAttribute("Image")) {
            var arr: Array<string> = node.getAttribute("Image").split(";");
            var arrTextures: Array<PIXI.Texture> = [];
            for (var i: number = 0; i < arr.length; i++) {
                var resourceId = arr[i];
                var texture: PIXI.Texture = StageController.getTextureByResourceId(resourceId);
                if (texture != null) {
                    arrTextures.push(texture);
                }
            }
            if (arrTextures != null && arrTextures.length > 0) {
                return new Proton.ImageTarget(arrTextures);
            } else {
                return null;
            }
        } else {
            if (DEBUG) {
                this.log("WARNING Image target has no attribute Image");
            }
        }

        return null;
    }

    /**
     * Add initializer animation as target for particle.
     * Example:
     *   <AnimationTarget Animation="[AnimationId]" Speed="1" Frames="numberOfFrames" Loop="false"/>
     * @method addAnimationTarget
     * @param node is Element from xml
     * @return void
     */
    private addAnimationTarget(node: Element): void {
        if (node.hasAttribute("Animation") && node.hasAttribute("Frames")) {
            if (node.hasAttribute("Speed")) {
                this._animationSpeed = parseFloat(node.getAttribute("Speed"));
            }

            if (node.hasAttribute("Loop")) {
                this._animationLoop = StringUtils.parseBoolean(node.getAttribute("Loop"));
            }

            this._animationName = node.getAttribute("Animation");

            this._textures = [];
            for (var i = 0; i < parseInt(node.getAttribute("Frames")); i++) {
                var val = i < 10 ? "0" + i : i;
                var texture: PIXI.Texture = PIXI.Texture.fromFrame(node.getAttribute("Animation") + "00" + val);
                this._textures.push(texture);
            }
        }
    }

    /**
     * Add initializer Mass.
     * Example:
     *   <Mass Mass="1;5" />
     * @method addMass
     * @param node is Element from xml
     * @return Proton.Mass
     */
    private addMass(node: Element): Proton.Mass {
        if (node.hasAttribute("Mass")) {
            var arr: Array<number> = ArrayUtils.numberArray(node.getAttribute("Mass"));
            if (arr.length === 3) {
                return new Proton.Mass(arr[0], arr[1], arr[2]);
            } else if (arr.length === 2) {
                return new Proton.Mass(arr[0], arr[1]);
            } else if (arr.length === 1) {
                return new Proton.Mass(arr[0]);
            } else {
                if (DEBUG) {
                    this.log("WARNING Mass attribute doesnt have parameters");
                }
            }
        } else {
            if (DEBUG) {
                this.log("WARNING no attribute Mass in Mass initializer");
            }
        }
        return null;
    }

    /**
     * Add initializer Radius.
     * Example:
     *   <Radius Radius="50;130" />
     * @method addRadius
     * @param node is Element from xml
     * @return Proton.Radius
     */
    private addRadius(node: Element): Proton.Radius {
        if (node.hasAttribute("Radius")) {
            var arr: Array<number> = ArrayUtils.numberArray(node.getAttribute("Radius"));
            if (arr.length === 3) {
                return new Proton.Radius(arr[0], arr[1], arr[2]);
            } else if (arr.length === 2) {
                return new Proton.Radius(arr[0], arr[1]);
            } else if (arr.length === 1) {
                return new Proton.Radius(arr[0]);
            } else {
                if (DEBUG) {
                    this.log("WARNING Radius attribute doesnt have parameters");
                }
            }
        } else {
            if (DEBUG) {
                this.log("WARNING no attribute Radius in Radius initializer");
            }
        }
        return null;
    }

    /**
     * Add initializer Life.
     * Example:
     *   <Life Life="20" />
     * @method addLife
     * @param node is Element from xml
     * @return Proton.Life
     */
    private addLife(node: Element): Proton.Life {
        if (node.hasAttribute("Life")) {
            var arr: Array<number> = ArrayUtils.numberArray(node.getAttribute("Life"));
            if (arr.length === 3) {
                return new Proton.Life(arr[0], arr[1], arr[2]);
            } else if (arr.length === 2) {
                return new Proton.Life(arr[0], arr[1]);
            } else if (arr.length === 1) {
                return new Proton.Life(arr[0]);
            } else {
                if (DEBUG) {
                    this.log("WARNING Life attribute doesnt have parameters");
                }
            }
        } else {
            if (DEBUG) {
                this.log("WARNING no attribute Life in Life initializer");
            }
        }
        return null;
    }

    /**
     * Add initializer Position. Position use zones. See method getZone().
     * Example:
     *   <Position>
     *       <RectZone X="0" Y="0" Width="1000" Height="1000" />
     *   </Position>
     * @method addPosition
     * @param node is Element from xml
     * @return Proton.Position
     */
    private addPosition(node: Element): Proton.Position {
        var zone: Element = node.firstElementChild;
        if (zone != null) {
            return new Proton.Position(this.getZone(zone));
        } else {
            if (DEBUG) {
                this.log("WARNING Position has no zone");
            }
        }
        return null;
    }

    /**
     * Add initializer Velocity.
     * Example:
     *   <Velocity RPan="0.1;0.3" ThaPan="0;360" Type="polar" />
     * @method addVelocity
     * @param node is Element from xml
     * @return Proton.Velocity
     */
    private addVelocity(node: Element): Proton.Velocity {
        var rpan: Array<number>;
        var thapan: Array<number>;
        var type: string;

        if (node.getAttribute("RPan")) {
            rpan = ArrayUtils.numberArray(node.getAttribute("RPan"));
        } else {
            if (DEBUG) {
                this.log("WARNING no attribute RPan in Velocity");
            }
        }

        if (node.getAttribute("ThaPan")) {
            thapan = ArrayUtils.numberArray(node.getAttribute("ThaPan"));
        } else {
            if (DEBUG) {
                this.log("WARNING no attribute ThaPan in Velocity");
            }
        }

        if (node.getAttribute("Type")) {
            type = node.getAttribute("Type");
        }

        if (rpan != null && thapan != null) {
            var rpanSpan: any;
            if (rpan.length === 2) {
                rpanSpan = new Proton.Span(rpan[0], rpan[1]);
            } else {
                rpanSpan = rpan[0];
            }

            var thapanSpan: any;
            if (thapan.length === 2) {
                thapanSpan = new Proton.Span(thapan[0], thapan[1]);
            } else {
                thapanSpan = thapan[0];
            }
            if (type != null) {
                return new Proton.Velocity(rpanSpan, thapanSpan, type);
            } else {
                return new Proton.Velocity(rpanSpan, thapanSpan);
            }
        } else {
            if (DEBUG) {
                this.log("WARNING missing some attribute in Velocity");
            }
        }

        return null;
    }

    /**
     * Return zone from xml.
     * @method getZone
     * @param node is Element from xml
     * @return any
     */
    private getZone(node: Element): any {
        switch (node.nodeName) {
            case "RectZone":
                var rectZoneX: number;
                var rectZoneY: number;
                var rectZoneWidth: number;
                var rectZoneHeight: number;

                if (node.hasAttribute("X")) {
                    rectZoneX = parseInt(node.getAttribute("X"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute X in RectZone");
                    }
                }

                if (node.hasAttribute("Y")) {
                    rectZoneY = parseInt(node.getAttribute("Y"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute Y in RectZone");
                    }
                }

                if (node.hasAttribute("Width")) {
                    rectZoneWidth = parseInt(node.getAttribute("Width"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute Width in RectZone");
                    }
                }

                if (node.hasAttribute("Height")) {
                    rectZoneHeight = parseInt(node.getAttribute("Height"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute Height in RectZone");
                    }
                }

                if (rectZoneX != null && rectZoneY != null && rectZoneWidth != null && rectZoneHeight != null) {
                    return new Proton.RectZone(rectZoneX, rectZoneY, rectZoneWidth, rectZoneHeight);
                } else {
                    if (DEBUG) {
                        this.log("WARNING missing some attributes in RectZone");
                    }
                }
                break;
            case "LineZone":
                var lineZoneX: number;
                var lineZoneY: number;
                var lineZoneWidth: number;
                var lineZoneHeight: number;
                var lineZoneDirection: string;

                if (node.hasAttribute("X")) {
                    lineZoneX = parseInt(node.getAttribute("X"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute X in LineZone");
                    }
                }

                if (node.hasAttribute("Y")) {
                    lineZoneY = parseInt(node.getAttribute("Y"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute Y in LineZone");
                    }
                }

                if (node.hasAttribute("Width")) {
                    lineZoneWidth = parseInt(node.getAttribute("Width"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute Width in LineZone");
                    }
                }

                if (node.hasAttribute("Height")) {
                    lineZoneHeight = parseInt(node.getAttribute("Height"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute Height in LineZone");
                    }
                }

                if (node.hasAttribute("Direction")) {
                    lineZoneDirection = node.getAttribute("Direction");
                }

                if (lineZoneX != null && lineZoneY != null && lineZoneWidth != null && lineZoneHeight != null) {
                    if (lineZoneDirection != null) {
                        return new Proton.LineZone(
                            lineZoneX,
                            lineZoneY,
                            lineZoneWidth,
                            lineZoneHeight,
                            lineZoneDirection,
                        );
                    } else {
                        return new Proton.LineZone(lineZoneX, lineZoneY, lineZoneWidth, lineZoneHeight);
                    }
                } else {
                    if (DEBUG) {
                        this.log("WARNING missing some attributes in LineZone");
                    }
                }

                break;
            case "CircleZone":
                var circleZoneX: number;
                var circleZoneY: number;
                var circleZoneRadius: number;

                if (node.hasAttribute("X")) {
                    circleZoneX = parseInt(node.getAttribute("X"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute X in CircleZone");
                    }
                }

                if (node.hasAttribute("Y")) {
                    circleZoneY = parseInt(node.getAttribute("Y"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute Y in CircleZone");
                    }
                }

                if (node.hasAttribute("Radius")) {
                    circleZoneRadius = parseInt(node.getAttribute("Radius"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute Radius in CircleZone");
                    }
                }

                if (circleZoneX != null && circleZoneY != null && circleZoneRadius != null) {
                    return new Proton.CircleZone(circleZoneX, circleZoneY, circleZoneRadius);
                } else {
                    if (DEBUG) {
                        this.log("WARNING missing some attributes in CircleZone");
                    }
                }
                break;
            case "PointZone":
                let pointZoneX: number;
                let pointZoneY: number;

                if (node.hasAttribute("X")) {
                    pointZoneX = parseInt(node.getAttribute("X"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute X in PointZone");
                    }
                }

                if (node.hasAttribute("Y")) {
                    pointZoneY = parseInt(node.getAttribute("Y"));
                } else {
                    if (DEBUG) {
                        this.log("WARNING no attribute Y in PointZone");
                    }
                }

                if (pointZoneX != null && pointZoneY != null) {
                    return new Proton.PointZone(pointZoneX, pointZoneY);
                } else {
                    if (DEBUG) {
                        this.log("WARNING missing some attributes in PointZone");
                    }
                }

                break;
            case "ImageZone":
                /*let x: number;
                let y: number;

                if (node.hasAttribute('X')) {
                    x = parseInt(node.getAttribute('X'));
                } else {
                    if (DEBUG) {
                        this.log('WARNING no attribute X in PointZone');
                    }
                }

                if (node.hasAttribute('Y')) {
                    y = parseInt(node.getAttribute('Y'));
                } else {
                    if (DEBUG) {
                        this.log('WARNING no attribute Y in PointZone');
                    }
                }*/

                if (node.hasAttribute("Image")) {
                    let texture: PIXI.Texture = StageController.getTextureByResourceId(node.getAttribute("Image"));
                    let particleSprite = new PIXI.Sprite(texture);
                    let pixelData: number[] = StageController.Renderer.extract.pixels(particleSprite);

                    return new Proton.ImageZone(
                        { data: pixelData, width: particleSprite.width, height: particleSprite.height },
                        0,
                        0,
                    );
                } else {
                    if (DEBUG) {
                        this.log("WARNING missing some attributes in ImageZone");
                    }
                }

                break;
            default:
                if (DEBUG) {
                    this.log("WARNING unknown type zone: " + node.nodeName);
                }
        }
        return null;
    }

    /**
     * Create behaviours from xml for emitter instance.
     * This bevaiours will define behaviour of particle when particle is alive.
     * @method createBehaviours
     * @param node is Element from xml
     * @param emitter Proton.Emitter
     * @return void
     */
    private createBehaviours(node: Element, emitter: Proton.Emitter): void {
        var next: Element = node.firstElementChild;
        while (next != null) {
            switch (next.nodeName) {
                case "AlphaDead":
                    if (next.hasAttribute("Age")) {
                        emitter.addBehaviour(this.alphaDead(parseFloat(next.getAttribute("Age"))));
                    } else {
                        emitter.addBehaviour(this.alphaDead(1));
                    }
                    break;
                case "ScaleDead":
                    emitter.addBehaviour(this.scaleDead());
                    break;
                case "Alpha":
                    var alpha: Proton.Alpha = this.addAlpha(next);
                    if (alpha != null) {
                        emitter.addBehaviour(alpha);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Alpha is null");
                        }
                    }
                    break;
                case "Scale":
                    var scale: Proton.Scale = this.addScale(next);
                    if (scale != null) {
                        emitter.addBehaviour(scale);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Scale is null");
                        }
                    }
                    break;
                case "Rotate":
                    var rotate: Proton.Rotate = this.addRotate(next);
                    if (rotate != null) {
                        emitter.addBehaviour(rotate);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Rotate is null");
                        }
                    }
                    break;
                case "Color":
                    var color: Proton.Color = this.addColor(next);
                    if (color != null) {
                        emitter.addBehaviour(color);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Color is null");
                        }
                    }
                    break;
                case "CrossZone":
                    var crossZone: Proton.CrossZone = this.addCrossZone(next);
                    if (crossZone != null) {
                        emitter.addBehaviour(crossZone);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING CrossZone is null");
                        }
                    }
                    break;
                case "RandomDrift":
                    var randomDrift: Proton.RandomDrift = this.addRandomDrift(next);
                    if (randomDrift != null) {
                        emitter.addBehaviour(randomDrift);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING RandomDrift is null");
                        }
                    }
                    break;
                case "Gravity":
                    var gravity: Proton.Gravity = this.addGravity(next);
                    if (gravity != null) {
                        emitter.addBehaviour(gravity);
                    } else {
                        if (DEBUG) {
                            this.log("WARNING Gravity is null");
                        }
                    }
                    break;
                default:
                    if (DEBUG) {
                        this.log("WARNING unknown behaviour");
                    }
            }
            next = next.nextElementSibling;
        }
    }

    /**
     * AlphaDead is custom custom dead behaviour. After particle reach some age particle will fade out to alpha 0.
     * Example:
     *   <AlphaDead Age="10" />
     * @method alphaDead
     * @param age number
     * @return any
     */
    alphaDead(age: number): any {
        var self: any = this;
        return {
            initialize: function (particle: any) {},
            applyBehaviour: function (particle: any) {
                if (particle.age >= age) {
                    particle["transform"]["alphaA"] = 1;
                    particle["transform"]["alphaB"] = 0;
                }

                if (particle.age >= particle.life) {
                    if (particle.sprite != null) {
                        if (self._textures != null && self._textures.length > 0) {
                            ProtonElement.Pool[self._animationName].push(particle.sprite);
                        } else {
                            ProtonElement.Pool[particle.target.textureCacheIds[0]].push(particle.sprite);
                        }
                        self.super2.removeChild(particle.sprite);
                    }
                    particle.sprite = null;
                    particle.dead = true;
                    particle = null;
                }
            },
        };
    }

    /**
     * ScaleDead is custom custom dead behaviour. After particle reach some energy particle will scale down to zero.
     * Example:
     *   <ScaleDead />
     * @method scaleDead
     * @return any
     */
    scaleDead(): any {
        var self: any = this;
        return {
            initialize: function (particle: any) {
                particle.scale = 0;
            },
            applyBehaviour: function (particle: any) {
                if (particle.energy >= 2 / 3) {
                    particle.scale = particle.scale * (1 - particle.energy) * 3;
                } else if (particle.energy <= 1 / 3) {
                    particle.scale = particle.scale * (particle.energy * 3);
                }
                if (particle.scale < 0) {
                    if (particle.sprite != null) {
                        if (this._textures != null && this._textures.length > 0) {
                            ProtonElement.Pool[this._animationName].push(particle.sprite);
                        } else {
                            ProtonElement.Pool[particle.target.textureCacheIds[0]].push(particle.sprite);
                        }
                        self.super2.removeChild(particle.sprite);
                    }
                    particle.sprite = null;
                    particle.dead = true;
                    particle = null;
                }
            },
        };
    }

    /**
     * Add behaviour Alpha.
     * Example:
     *   <Alpha Start="0" End="1" Life="5" />
     * @method addAlpha
     * @param node is Element from xml
     * @return Proton.Alpha
     */
    private addAlpha(node: Element): Proton.Alpha {
        var start: number;
        var end: number;
        var life: Proton.Life;

        if (node.hasAttribute("Start")) {
            start = parseFloat(node.getAttribute("Start"));
        } else {
            if (DEBUG) {
                this.log("WARNING Alpha has no attribute Start");
            }
        }

        if (node.hasAttribute("End")) {
            end = parseFloat(node.getAttribute("End"));
        } else {
            if (DEBUG) {
                this.log("WARNING Alpha has no attribute End");
            }
        }

        if (node.hasAttribute("Life")) {
            life = new Proton.Life(parseFloat(node.getAttribute("Life")));
        }

        if (start != null && end != null) {
            if (life != null) {
                return new Proton.Alpha(start, end, life);
            } else {
                return new Proton.Alpha(start, end);
            }
        } else {
            if (DEBUG) {
                this.log("WARNING Alpha has no Start or End attribute");
            }
        }

        return null;
    }

    /**
     * Add behaviour Scale.
     * Example:
     *   <Scale Start="1" End="1.5" />
     * @method addScale
     * @param node is Element from xml
     * @return Proton.Scale
     */
    private addScale(node: Element): Proton.Scale {
        var start: Array<number>;
        var end: Array<number>;
        var life: Proton.Life;

        if (node.hasAttribute("Start")) {
            start = ArrayUtils.numberArray(node.getAttribute("Start"));
        } else {
            if (DEBUG) {
                this.log("WARNING Scale has no attribute Start");
            }
        }

        if (node.hasAttribute("End")) {
            end = ArrayUtils.numberArray(node.getAttribute("End"));
        }

        if (node.hasAttribute("Life")) {
            life = new Proton.Life(parseFloat(node.getAttribute("Life")));
        }

        if (start != null) {
            var startSpan: any;
            if (start.length === 2) {
                startSpan = new Proton.Span(start[0], start[1]);
            } else {
                startSpan = start[0];
            }

            var endSpan: any;
            if (end != null) {
                if (end.length === 2) {
                    endSpan = new Proton.Span(end[0], end[1]);
                } else {
                    endSpan = end[0];
                }
            }

            if (startSpan != null && endSpan != null && life != null) {
                return new Proton.Scale(startSpan, endSpan, life);
            } else if (startSpan != null && endSpan != null) {
                return new Proton.Scale(startSpan, endSpan);
            } else {
                return new Proton.Scale(startSpan);
            }
        } else {
            if (DEBUG) {
                this.log("WARNING Scale has no Start or End attribute");
            }
        }

        return null;
    }

    /**
     * Add behaviour Rotate.
     * Example:
     *   <Rotate Start="0;360" End="-0.5;0.5" Style="add" />
     * @method addRotate
     * @param node is Element from xml
     * @return Proton.Rotate
     */
    private addRotate(node: Element): Proton.Rotate {
        var start: Array<number>;
        var end: Array<number>;
        var style: string;

        if (node.hasAttribute("Start")) {
            start = ArrayUtils.numberArray(node.getAttribute("Start"));
        } else {
            if (DEBUG) {
                this.log("WARNING Rotate has no attribute Start");
            }
        }

        if (node.hasAttribute("End")) {
            end = ArrayUtils.numberArray(node.getAttribute("End"));
        } else {
            if (DEBUG) {
                this.log("WARNING Rotate has no attribute End");
            }
        }

        if (node.hasAttribute("Style")) {
            style = node.getAttribute("Style");
        } else {
            if (DEBUG) {
                this.log("WARNING Style has no attribute End");
            }
        }

        if (start != null && end != null && style != null) {
            var startSpan: any;
            if (start.length === 2) {
                startSpan = new Proton.Span(start[0], start[1]);
            } else {
                startSpan = start[0];
            }

            var endSpan: any;
            if (end.length === 2) {
                endSpan = new Proton.Span(end[0], end[1]);
            } else {
                endSpan = start[0];
            }

            return new Proton.Rotate(startSpan, endSpan, style);
        } else {
            if (DEBUG) {
                this.log("WARNING Rotate has no Start or End attribute");
            }
        }

        return null;
    }

    /**
     * Add behaviour RandomDrift.
     * Example:
     *   <RandomDrift DriftX="5" DriftY="0" Delay="0.15"/>
     * @method addRandomDrift
     * @param node is Element from xml
     * @return Proton.RandomDrift
     */
    private addRandomDrift(node: Element): Proton.RandomDrift {
        var driftX: number;
        var driftY: number;
        var delay: number;

        if (node.hasAttribute("DriftX")) {
            driftX = parseFloat(node.getAttribute("DriftX"));
        } else {
            if (DEBUG) {
                this.log("WARNING RandomDrift has no attribute DriftX");
            }
        }

        if (node.hasAttribute("DriftY")) {
            driftY = parseFloat(node.getAttribute("DriftY"));
        } else {
            if (DEBUG) {
                this.log("WARNING RandomDrift has no attribute DriftY");
            }
        }

        if (node.hasAttribute("Delay")) {
            delay = parseFloat(node.getAttribute("Delay"));
        } else {
            if (DEBUG) {
                this.log("WARNING RandomDrift has no attribute Delay");
            }
        }

        if (driftX != null && driftY != null && delay != null) {
            return new Proton.RandomDrift(driftX, driftY, delay);
        } else {
            if (DEBUG) {
                this.log("WARNING RandomDrift has no valid attributes");
            }
        }

        return null;
    }

    /**
     * Add behaviour Gravity.
     * Example:
     *   <Gravity Gravity="5" />
     * @method addGravity
     * @param node is Element from xml
     * @return Proton.Gravity
     */
    private addGravity(node: Element): Proton.Gravity {
        var gravity: number;

        if (node.hasAttribute("Gravity")) {
            gravity = parseFloat(node.getAttribute("Gravity"));
        } else {
            if (DEBUG) {
                this.log("WARNING Gravity has no attribute Gravity");
            }
        }

        if (gravity != null) {
            return new Proton.Gravity(gravity);
        } else {
            if (DEBUG) {
                this.log("WARNING Gravity has no valid attributes");
            }
        }

        return null;
    }

    /**
     * Add behaviour Color.
     * Example:
     *   <Color Color="FFFFFF" EndColor="000000" />
     * @method addColor
     * @param node is Element from xml
     * @return Proton.Color
     */
    private addColor(node: Element): Proton.Color {
        var i: number;
        var arr: Array<string>;
        var endArr: Array<string>;
        var color: Array<string> = [];
        var endColor: Array<string> = [];

        if (node.hasAttribute("Color")) {
            arr = node.getAttribute("Color").split(";");
            for (i = 0; i < arr.length; i++) {
                color.push(StringUtils.hexColor(arr[i]));
            }
        } else {
            if (DEBUG) {
                this.log("WARNING Color has no attribute Color");
            }
        }

        if (node.hasAttribute("EndColor")) {
            endArr = node.getAttribute("EndColor").split(";");
            for (i = 0; i < endArr.length; i++) {
                endColor.push(StringUtils.hexColor(endArr[i]));
            }
        }

        if (color.length > 0) {
            if (endColor.length > 0) {
                return new Proton.Color(color, endColor);
            } else {
                return new Proton.Color(color);
            }
        } else {
            if (DEBUG) {
                this.log("WARNING Color has no Color attribute");
            }
        }

        return null;
    }

    /**
     * Add behaviour CrossZone. CrossZone is used for collisions. Need some zone. See method getZone().
     * Example:
     *   <CrossZone Type="bound">
     *       <LineZone X="0" Y="0" Width="1000" Height="1000" Direction="down" />
     *   </CrossZone>
     * @method addCrossZone
     * @param node is Element from xml
     * @return Proton.CrossZone
     */
    private addCrossZone(node: Element): Proton.CrossZone {
        var zone: any;
        var type: string;

        if (node.firstElementChild != null) {
            zone = this.getZone(node.firstElementChild);
        } else {
            if (DEBUG) {
                this.log("WARNING no zone in CrossZone");
            }
        }

        if (node.hasAttribute("Type")) {
            type = node.getAttribute("Type");
        }

        if (zone != null) {
            if (type != null) {
                return new Proton.CrossZone(zone, type);
            } else {
                return new Proton.CrossZone(zone);
            }
        } else {
            if (DEBUG) {
                this.log("WARNING no zone in CrossZone");
            }
        }

        return null;
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @return void
     */
    protected log(s: string): void {
        if (DEBUG) {
            var compName = (this as any).__componentName__;
            Logger.debugGroup(compName, s);
        }
    }

    clone(): ProtonElement {
        var element: ProtonElement = new ProtonElement();

        element.super2.position.x = this.super2.position.x;
        element.super2.position.y = this.super2.position.y;
        element.super2.pivot.x = this.super2.pivot.x;
        element.super2.pivot.y = this.super2.pivot.y;
        element.super2.scale.x = this.super2.scale.x;
        element.super2.scale.y = this.super2.scale.y;
        element.super2.visible = this.super2.visible;
        var node: Element = document.createElement("particle");
        node.setAttribute(ProtonElementAttributes.STYLE_2, this._styleName);
        if (this._blendMode != null) {
            node.setAttribute(ProtonElementAttributes.BLEND_MODE, this._blendMode.toString());
        }

        element.create(node);
        element.super2._markAsCloned(); // must be called last before return!
        return element;
    }
}

const ProtonElementAttributes = {
    EMIT: "Emit",
    /**
     * Tint of animation. Hex color which colorize movie clip.
     * @property Tint
     * @type string
     * @default null
     */
    TINT: "Tint",
    /**
     * Blend mode for animation.
     * Bleding modes:
     *   0 = NORMAL;
     *   1 = ADD;
     *   2 = MULTIPLY;
     *   3 = SCREEN;
     *   4 = OVERLAY;
     *   5 = DARKEN;
     *   6 = LIGHTEN;
     *   7 = COLOR_DODGE;
     *   8 = COLOR_BURN;
     *   9 = HARD_LIGHT;
     *   10 = SOFT_LIGHT;
     *   11 = DIFFERENCE;
     *   12 = EXCLUSION;
     *   13 = HUE;
     *   14 = SATURATION;
     *   15 = COLOR;
     *   16 = LUMINOSITY;
     * @property BlendMode
     * @type number
     * @default null
     */
    BLEND_MODE: "BlendMode",
    /**
     * Style define animation. You can use one style on more than one place.
     * Style for ParticleElement has to be instance of ParticleStyle.
     * @property Style
     * @type string
     * @default null
     */
    STYLE: "Style",
    STYLE_2: "Style2",
    START: "Start",
    STOPPING: "Stopping",
    STOP: "Stop",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Graphics/ProtonElement.ts
 **/